import { useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';

import { useAccountSwitcher } from '@/hooks/useAccountSwitcher';
import { Sentry } from '@/util/sentry';

/**
 * Acts as the shared account a link names in `actAs` before its conversation loads.
 *
 * The text staff get for a shared account such as Ask Anonymously links to a
 * conversation that belongs to the account, not to them. Signed in as
 * themselves they are not part of it, so the link names the account and this
 * switches to it, exactly as choosing it in the account switcher would. They
 * stay signed in as themselves.
 *
 * The id is not a credential. Only the shared-texts account the API offers this
 * person is switched to: the API guarantees it is one they may act as and never
 * their own. Any other id, a support account included, is ignored and the
 * conversation opens as whoever is acting now, since a hand-made link must not
 * move someone into an inbox app-wide and have it persist.
 *
 * The link is acted on once. `actAs` is cleared from the route as soon as it is
 * handled, switched or refused, so a later switch back to self is not undone by
 * returning to the screen, a reload or browser history, and never blanks it.
 *
 * Returns whether the screen should wait: while the offered account is unknown,
 * and until an allowed switch has landed.
 */
export function useActAsFromLink(actAs: string | undefined): { isSwitching: boolean } {
  const router = useRouter();

  const { actAsUserId, isTargetsLoading, isTargetsError, sharedTextsAccountId, switchToAccount } =
    useAccountSwitcher();

  /** The id already handled, so a re-render before the param clears does not act twice. */
  const handled = useRef<string | undefined>(undefined);

  const isPending = !!actAs && handled.current !== actAs;

  const isAllowed = !!actAs && actAs === sharedTextsAccountId;

  useEffect(() => {
    // Cleared once handled, so a later link into the same open chat is acted on afresh.
    if (!actAs) {
      handled.current = undefined;

      return;
    }

    if (handled.current === actAs || isTargetsLoading) {
      return;
    }

    handled.current = actAs;

    // Emptied rather than unset: the router merges params, so `undefined` leaves the old value.
    const clearParam = () => router.setParams({ actAs: '' });

    if (actAsUserId === actAs) {
      clearParam();

      return;
    }

    if (!isAllowed) {
      Sentry.addBreadcrumb({
        category: 'impersonation',
        level: 'warning',
        message: "Skipped the link's account switch",
        data: { reason: isTargetsError ? 'targets-unavailable' : 'not-offered' },
      });

      clearParam();

      return;
    }

    switchToAccount(actAs)
      .catch((error: unknown) => {
        Sentry.captureException(error, {
          tags: { component: 'useActAsFromLink', source: 'inbox-chat-link' },
          extra: { errorMessage: error instanceof Error ? error.message : undefined },
        });
      })
      .finally(clearParam);
  }, [actAs, actAsUserId, isAllowed, isTargetsError, isTargetsLoading, router, switchToAccount]);

  // The switcher sets the acting id before anything that can fail, so an allowed
  // switch ends the wait at once; a refused link never waits beyond the lookup.
  return { isSwitching: isPending && actAsUserId !== actAs && (isTargetsLoading || isAllowed) };
}
